import { useEffect, useMemo, useState } from "react";
import { Link, useParams } from "react-router-dom";
import SiteLayout from "@/components/feature/SiteLayout";
import Breadcrumbs from "@/components/feature/Breadcrumbs";
import WorkCard from "@/components/feature/WorkCard";
import { movies, tvShows } from "@/mocks/works";
import type { Work } from "@/types/content";
import { usePageMeta } from "@/hooks/usePageMeta";

const yearRanges = [
  { value: "all", label: "全部年份" },
  { value: "2025-2026", label: "2025 - 2026" },
  { value: "2022-2024", label: "2022 - 2024" },
  { value: "2019-2021", label: "2019 - 2021" },
  { value: "2015-2018", label: "2015 - 2018" },
];

const statusOptions = [
  { value: "all", label: "全部状态" },
  { value: "new", label: "新作（2023 及以后）" },
  { value: "classic", label: "经典作品（2022 及以前）" },
];

const sortOptions = [
  { value: "latest", label: "按上映时间（新 → 旧）" },
  { value: "earliest", label: "按上映时间（旧 → 新）" },
  { value: "title", label: "按作品名称排序" },
];

const PAGE_SIZE = 8;

function inRange(year: number, range: string) {
  if (range === "all") return true;
  const [start, end] = range.split("-").map((item) => Number(item));
  return year >= start && year <= end;
}

export default function CategoryPage() {
  const { type } = useParams<{ type: string }>();
  const isTv = type === "tv";
  const source: Work[] = isTv ? tvShows : movies;

  const [yearRange, setYearRange] = useState("all");
  const [status, setStatus] = useState("all");
  const [sort, setSort] = useState("latest");
  const [visible, setVisible] = useState(PAGE_SIZE);
  const [filtersOpen, setFiltersOpen] = useState(false);

  const title = isTv ? "电视剧作品" : "电影作品";

  usePageMeta(
    isTv
      ? "大桥未久电视剧作品大全 - 大桥未久影视网站"
      : "大桥未久电影作品大全 - 大桥未久影视网站",
    isTv
      ? "整理大桥未久参演的电视剧作品资料，包含首播年份、类型、集数、剧情简介与演职员信息，支持按年份与状态筛选。"
      : "整理大桥未久参演的电影作品资料，包含上映年份、类型、片长、剧情简介与演职员信息，支持按年份与状态筛选。",
    isTv
      ? "大桥未久电视剧,剧集作品,电视剧介绍,演员资料,作品列表"
      : "大桥未久电影,电影作品,电影介绍,作品列表,上映年份",
  );

  useEffect(() => {
    setYearRange("all");
    setStatus("all");
    setSort("latest");
    setVisible(PAGE_SIZE);
    setFiltersOpen(false);
  }, [type]);

  useEffect(() => {
    setVisible(PAGE_SIZE);
  }, [yearRange, status, sort]);

  const filtered = useMemo(() => {
    const result = source.filter(
      (work) =>
        inRange(work.year, yearRange) && (status === "all" || work.status === status),
    );

    return result.sort((a, b) => {
      if (sort === "earliest") return a.year - b.year;
      if (sort === "title") return a.title.localeCompare(b.title, "zh-Hans-CN");
      return b.year - a.year;
    });
  }, [source, yearRange, status, sort]);

  const shown = filtered.slice(0, visible);
  const hasMore = visible < filtered.length;

  const resetFilters = () => {
    setYearRange("all");
    setStatus("all");
    setSort("latest");
  };

  const isFiltered = yearRange !== "all" || status !== "all" || sort !== "latest";

  return (
    <SiteLayout>
      <section className="w-full border-b border-background-200 bg-background-100/60">
        <div className="mx-auto w-full max-w-[1280px] px-4 pb-8 pt-6 md:px-6 md:pb-10 md:pt-8">
          <Breadcrumbs
            items={[
              { label: "首页", to: "/" },
              { label: isTv ? "电视剧作品" : "电影作品" },
            ]}
          />

          <div className="mt-6 flex flex-col gap-6 lg:flex-row lg:items-end lg:justify-between">
            <div className="max-w-2xl">
              <p className="font-label text-[11px] font-semibold uppercase tracking-[0.18em] text-accent-700">
                {isTv ? "Television works" : "Feature films"}
              </p>
              <h1 className="mt-3 text-2xl font-bold tracking-tight text-foreground-950 md:text-[32px]">
                大桥未久{title}大全
              </h1>
              <p className="mt-3 text-sm leading-relaxed text-foreground-600">
                {isTv
                  ? "收录按首播时间整理的电视剧作品，包含集数、类型与剧情简介，可按年份区间与作品状态筛选。"
                  : "收录按上映时间整理的电影作品，包含片长、类型与剧情简介，可按年份区间与作品状态筛选。"}
              </p>
            </div>

            <div className="inline-flex w-full shrink-0 rounded-full border border-background-200 bg-background-50 px-1 py-1 sm:w-auto">
              <Link
                to="/category/movie"
                className={`flex min-h-[40px] flex-1 cursor-pointer items-center justify-center gap-2 whitespace-nowrap rounded-full px-5 text-sm transition-colors sm:flex-none ${
                  !isTv
                    ? "bg-primary-500 font-medium text-background-50"
                    : "text-foreground-600 hover:text-foreground-950"
                }`}
              >
                <i className="ri-film-line"></i>
                电影
              </Link>
              <Link
                to="/category/tv"
                className={`flex min-h-[40px] flex-1 cursor-pointer items-center justify-center gap-2 whitespace-nowrap rounded-full px-5 text-sm transition-colors sm:flex-none ${
                  isTv
                    ? "bg-primary-500 font-medium text-background-50"
                    : "text-foreground-600 hover:text-foreground-950"
                }`}
              >
                <i className="ri-tv-2-line"></i>
                电视剧
              </Link>
            </div>
          </div>
        </div>
      </section>

      <div className="mx-auto w-full max-w-[1280px] px-4 py-8 md:px-6 md:py-10">
        <div className="rounded-lg border border-background-200 bg-background-50">
          <div className="flex items-center justify-between gap-3 border-b border-background-200 px-4 py-3 md:hidden">
            <span className="flex items-center gap-2 text-sm text-foreground-700">
              <i className="ri-filter-3-line text-accent-600"></i>
              筛选与排序
            </span>
            <button
              type="button"
              onClick={() => setFiltersOpen((open) => !open)}
              aria-expanded={filtersOpen}
              className="flex min-h-[40px] cursor-pointer items-center gap-1 rounded-md border border-background-300 px-3 text-xs text-foreground-700"
            >
              {filtersOpen ? "收起" : "展开"}
              <i
                className={`ri-arrow-down-s-line transition-transform ${
                  filtersOpen ? "rotate-180" : ""
                }`}
              ></i>
            </button>
          </div>

          <div
            className={`grid gap-3 px-4 py-4 sm:grid-cols-2 lg:grid-cols-4 ${
              filtersOpen ? "grid" : "hidden md:grid"
            }`}
          >
            <label className="flex min-w-0 flex-col gap-1.5">
              <span className="text-[11px] text-foreground-500">年份区间</span>
              <select
                value={yearRange}
                onChange={(event) => setYearRange(event.target.value)}
                className="min-h-[44px] w-full cursor-pointer rounded-md border border-background-300 bg-background-50 px-3 text-sm text-foreground-800 focus:border-primary-400 focus:outline-none"
              >
                {yearRanges.map((item) => (
                  <option key={item.value} value={item.value}>
                    {item.label}
                  </option>
                ))}
              </select>
            </label>

            <label className="flex min-w-0 flex-col gap-1.5">
              <span className="text-[11px] text-foreground-500">作品状态</span>
              <select
                value={status}
                onChange={(event) => setStatus(event.target.value)}
                className="min-h-[44px] w-full cursor-pointer rounded-md border border-background-300 bg-background-50 px-3 text-sm text-foreground-800 focus:border-primary-400 focus:outline-none"
              >
                {statusOptions.map((item) => (
                  <option key={item.value} value={item.value}>
                    {item.label}
                  </option>
                ))}
              </select>
            </label>

            <label className="flex min-w-0 flex-col gap-1.5">
              <span className="text-[11px] text-foreground-500">排序方式</span>
              <select
                value={sort}
                onChange={(event) => setSort(event.target.value)}
                className="min-h-[44px] w-full cursor-pointer rounded-md border border-background-300 bg-background-50 px-3 text-sm text-foreground-800 focus:border-primary-400 focus:outline-none"
              >
                {sortOptions.map((item) => (
                  <option key={item.value} value={item.value}>
                    {item.label}
                  </option>
                ))}
              </select>
            </label>

            <div className="flex min-w-0 items-end">
              <button
                type="button"
                onClick={resetFilters}
                disabled={!isFiltered}
                className="flex min-h-[44px] w-full cursor-pointer items-center justify-center gap-2 rounded-md border border-background-300 text-sm text-foreground-700 transition-colors hover:border-accent-400 hover:text-accent-700 disabled:cursor-not-allowed disabled:opacity-45"
              >
                <i className="ri-refresh-line"></i>
                重置筛选
              </button>
            </div>
          </div>
        </div>

        <div className="mt-6 flex flex-wrap items-center justify-between gap-2 text-xs text-foreground-500">
          <p>
            共 <span className="font-label font-semibold text-foreground-900">{filtered.length}</span>{" "}
            部作品，已显示{" "}
            <span className="font-label font-semibold text-foreground-900">{shown.length}</span> 部
          </p>
          <p className="flex items-center gap-1.5">
            <i className="ri-information-line"></i>
            筛选与排序为前端即时处理，数据为演示内容
          </p>
        </div>

        {shown.length > 0 ? (
          <>
            <div className="mt-5 grid grid-cols-2 gap-4 sm:grid-cols-3 md:gap-6 lg:grid-cols-4">
              {shown.map((work, index) => (
                <WorkCard key={work.id} work={work} delay={index * 40} />
              ))}
            </div>

            {hasMore ? (
              <div className="mt-10 flex justify-center">
                <button
                  type="button"
                  onClick={() => setVisible((count) => count + PAGE_SIZE)}
                  className="inline-flex min-h-[48px] cursor-pointer items-center gap-2 whitespace-nowrap rounded-md border border-primary-300 bg-background-50 px-6 text-sm font-medium text-primary-700 transition-colors hover:border-primary-500 hover:bg-primary-50"
                >
                  加载更多作品
                  <i className="ri-arrow-down-line"></i>
                </button>
              </div>
            ) : (
              <p className="mt-10 text-center text-xs text-foreground-400">
                已展示全部 {filtered.length} 部作品
              </p>
            )}
          </>
        ) : (
          <div className="mt-8 flex flex-col items-center justify-center rounded-lg border border-dashed border-background-300 bg-background-50 px-6 py-16 text-center">
            <span className="flex h-14 w-14 items-center justify-center rounded-full bg-background-100 text-foreground-400">
              <i className="ri-search-eye-line text-2xl"></i>
            </span>
            <h2 className="mt-4 text-base font-semibold text-foreground-900">
              没有找到符合条件的作品
            </h2>
            <p className="mt-2 max-w-md text-sm leading-relaxed text-foreground-500">
              当前筛选条件下暂无结果，试试放宽年份区间，或重置筛选条件重新浏览全部作品。
            </p>
            <button
              type="button"
              onClick={resetFilters}
              className="mt-6 inline-flex min-h-[44px] cursor-pointer items-center gap-2 whitespace-nowrap rounded-md bg-primary-500 px-5 text-sm font-medium text-background-50 transition-colors hover:bg-primary-600"
            >
              <i className="ri-refresh-line"></i>
              重置筛选条件
            </button>
          </div>
        )}
      </div>
    </SiteLayout>
  );
}